import { useState, useEffect, useCallback } from "react";
import { useNavigate, Navigate } from "react-router";
import { useTranslation } from "react-i18next";
import { MailCheck, RefreshCw, AlertCircle, CheckCircle2 } from "lucide-react";
import { hasSignedInUser, reloadEmailVerified } from "../../application/session";
import { sendVerificationEmail } from "../../application/signUp";
import { useAuth } from "../../application/useAuth";
import AuthLayout from "./AuthPageLayout";
import Button from "../../../../shared/ui/button/Button";
import { LoadingOverlay } from "../../../../shared/ui/common/LoadingOverlay";

const RESEND_COOLDOWN_SECONDS = 60;

export default function VerifyEmailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [checkLoading, setCheckLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const handleResend = useCallback(async () => {
    // The request is only authenticated once Firebase has restored the session,
    // so a missing user is reported rather than swallowed by an early return.
    if (!user) {
      setError(t("auth.emailVerification.sessionExpired"));
      return;
    }
    setResendLoading(true);
    setError(null);
    setNotice(null);
    try {
      await sendVerificationEmail();
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setNotice(t("auth.emailVerification.resendSuccess"));
    } catch {
      setError(t("auth.emailVerification.resendError"));
    } finally {
      setResendLoading(false);
    }
  }, [user, t]);

  const handleConfirm = useCallback(async () => {
    if (!hasSignedInUser()) {
      setError(t("auth.emailVerification.sessionExpired"));
      return;
    }
    setCheckLoading(true);
    setError(null);
    setNotice(null);
    try {
      if (await reloadEmailVerified()) {
        navigate("/");
      } else {
        setError(t("auth.emailVerification.notVerifiedYet"));
      }
    } finally {
      setCheckLoading(false);
    }
  }, [navigate, t]);

  if (authLoading) {
    return <LoadingOverlay visible={true} message={t("common.loading")} />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AuthLayout>
      <div className="w-full space-y-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="p-4 rounded-full bg-brand-50 dark:bg-brand-500/10">
            <MailCheck className="w-10 h-10 text-brand-500" />
          </div>
          <h1 className="text-2xl font-bold text-content-primary">
            {t("auth.emailVerification.pageTitle")}
          </h1>
          <p className="text-content-tertiary text-sm max-w-sm">
            {t("auth.emailVerification.pageDesc")}
          </p>
        </div>

        {error && (
          <div className="p-3 rounded-xl bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/20 text-error-600 dark:text-error-400 text-sm flex gap-2 items-center">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        {notice && (
          <div className="p-3 rounded-xl bg-success-50 dark:bg-success-500/10 border border-success-200 dark:border-success-500/20 text-success-600 dark:text-success-400 text-sm flex gap-2 items-center">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            {notice}
          </div>
        )}

        <div className="flex flex-col gap-3">
          <Button
            className="w-full"
            onClick={handleConfirm}
            loading={checkLoading}
            startIcon={<RefreshCw className="w-4 h-4" />}
          >
            {t("auth.emailVerification.refresh")}
          </Button>

          <Button
            variant="outline"
            className="w-full"
            onClick={handleResend}
            disabled={cooldown > 0 || resendLoading}
            loading={resendLoading}
          >
            {cooldown > 0
              ? t("auth.emailVerification.resendIn", { seconds: cooldown })
              : t("auth.emailVerification.resend")}
          </Button>
        </div>
      </div>
    </AuthLayout>
  );
}
